<template>
  <div
    v-editable="service"
    class="flex flex-col h-full border border-[#E9EAEC] rounded-2xl p-3 bg-white shadow-0 relative"
  >
    <div class="flex-1">
      <div
        v-if="service.topPill"
        class="absolute inset-x-4 mb-4"
      >
        <div
          class="font-bold bg-energy-green text-white rounded-md px-2 py-0.5 text-center text-[10px]"
        >
          Most Popular
        </div>
      </div>

      <div
        class="flexflex-col items-center border-b border-[#E9EAEC] lg:pt-3"
      >
        <NuxtImg
          provider="storyblok"
          class="w-[227px] m-auto"
          width="300px"
          format="webp"
          :src="service.image.filename"
          :alt="getImageAlt(service.image)"
          loading="lazy"
        />

        <div class="pb-3 text-left lg:text-center">
          <h4 class="subtitle-4">{{ service.title }}</h4>
          <p class="italic mt-2 text-xs">{{ service.subtitle }}</p>
        </div>
      </div>

      <div class="p-[16px] secondary">
        <p class="text-sm">Perfect for:</p>
        <div class="prose prose-lg max-w-none service-card-rich-text">
          <Vue3RuntimeTemplate :template="resolvedRichText" />
        </div>

        <div
          class="inline-flex items-center space-x-[8px] bg-light-green px-2 py-1 rounded-full mt-2"
        >
          <CommonSVGTemplate
            width="24"
            height="24"
            alt="Bin Bags"
            :path="binBag"
          />
          <p class="font-semibold secondary pr-2">
            {{ service.tag }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex space-x-3 mt-auto px-[6px] text-sm">
      <Button
        intent="secondary"
        size="md"
        class="!w-[180px] font-semibold"
        as="nuxt-link"
        :to="service.hireNowLink"
      >
        Hire Now
      </Button>

      <Button
        class="w-full font-semibold"
        size="md"
        as="nuxt-link"
        :to="service.viewDetailsLink"
      >
        <span class="flex items-center">
          View details
          <CommonSVGTemplate
            width="20"
            height="20"
            alt="Arrow"
            :path="arrow"
          />
        </span>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Vue3RuntimeTemplate from "vue3-runtime-template";

interface Service {
  image: {
    filename: string;
    alt?: string;
  };
  title: string;
  subtitle: string;
  list: any;
  tag: string;
  hireNowLink: string;
  viewDetailsLink: string;
  topPill?: boolean;
}

const props = defineProps({
  service: {
    type: Object as () => Service,
    required: true
  }
});

const { getImageAlt } = useImageAlt();

// Resolve the rich text (service.list) using the Storyblok renderer
const resolvedRichText = computed(() =>
  renderRichText(props.service.list, {
    resolver: (component, blok) => {
      return `<component :blok='${JSON.stringify(
        blok
      )}' is="${component}"></component>`;
    },
  })
);

// SVG paths for bin bag and arrow icons
const binBag = `
<path d="M21.1267 20.3437C20.9674 19.8768 20.6707 19.4689 20.2755 19.1737C19.1842 18.0712 20.0317 16.23 19.8892 14.2537C19.6191 10.5264 17.0551 7.66417 14.1292 6.26247C12.6008 5.53349 11.1873 5.3417 9.6292 5.96997C6.82882 7.08579 4.14458 10.5974 4.0042 14.3587C3.9217 16.5637 4.51795 18.315 3.6967 19.4587C3.36295 19.9237 2.7292 20.4 2.8192 21.0487C2.86689 21.2437 2.95319 21.4271 3.07299 21.5881C3.1928 21.7491 3.34367 21.8845 3.5167 21.9862C4.7692 22.8525 6.67045 22.3312 8.1517 22.065C10.758 21.6451 13.4125 21.6186 16.0267 21.9862C18.4755 22.2825 20.0692 22.8675 20.8155 21.9862C20.9859 21.7526 21.1022 21.4841 21.156 21.2C21.2099 20.9159 21.1999 20.6234 21.1267 20.3437ZM8.65045 7.28622C8.91172 7.09824 9.1876 6.93146 9.47545 6.78747C9.56386 6.74368 9.66599 6.73659 9.7596 6.76775C9.85321 6.79892 9.93071 6.86581 9.97522 6.95385C10.0197 7.04189 10.0277 7.14396 9.99727 7.23782C9.96688 7.33168 9.90063 7.40973 9.81295 7.45497C9.55965 7.58392 9.31645 7.73184 9.08545 7.89747C9.00435 7.95389 8.90427 7.97603 8.80693 7.9591C8.7096 7.94216 8.62287 7.88752 8.56559 7.80702C8.50831 7.72653 8.48509 7.62669 8.50098 7.52918C8.51687 7.43167 8.57058 7.34436 8.65045 7.28622ZM6.44545 11.0475C6.4014 11.1365 6.32389 11.2044 6.22987 11.2365C6.13585 11.2685 6.03298 11.262 5.94375 11.2183C5.85452 11.1747 5.78619 11.0975 5.75372 11.0037C5.72124 10.9098 5.72726 10.8069 5.77045 10.7175C6.25433 9.71546 6.91925 8.81153 7.7317 8.05122C7.80343 7.9832 7.89916 7.94635 7.99797 7.94871C8.09679 7.95108 8.19065 7.99246 8.25904 8.06383C8.32743 8.13519 8.36478 8.23073 8.36294 8.32955C8.3611 8.42838 8.32021 8.52245 8.2492 8.59122C7.50291 9.29287 6.89156 10.1254 6.44545 11.0475ZM10.8967 18.3037H8.74045C8.55617 18.3147 8.37226 18.2768 8.20738 18.1937C8.0425 18.1107 7.9025 17.9855 7.8016 17.831C7.7007 17.6764 7.64247 17.4978 7.63283 17.3135C7.62319 17.1291 7.66248 16.9455 7.7467 16.7812L8.63545 15.1987L8.30918 15.2775C8.21246 15.3001 8.11069 15.2836 8.0261 15.2315C7.94151 15.1795 7.88097 15.096 7.85769 14.9994C7.83441 14.9028 7.85029 14.801 7.90186 14.7161C7.95343 14.6312 8.0365 14.5701 8.13293 14.5462L9.28418 14.2687C9.37361 14.2461 9.46826 14.2578 9.54948 14.3016C9.6307 14.3453 9.69258 14.4179 9.72293 14.505L10.1204 15.5887C10.1535 15.6819 10.1485 15.7844 10.1064 15.8739C10.0644 15.9634 9.98875 16.0327 9.89592 16.0668C9.80308 16.1009 9.70055 16.097 9.61059 16.0559C9.52063 16.0148 9.4505 15.9399 9.41543 15.8475L9.30293 15.5362L8.40293 17.1487C8.28668 17.3512 8.50043 17.5537 8.74043 17.5537H10.8967C10.9961 17.5537 11.0915 17.5932 11.1618 17.6635C11.2322 17.7339 11.2717 17.8293 11.2717 17.9287C11.2717 18.0282 11.2322 18.1235 11.1619 18.1939C11.0915 18.2642 10.9962 18.3037 10.8967 18.3037ZM11.6617 11.3212L10.368 13.635C10.3187 13.7209 10.2374 13.784 10.1419 13.8104C10.0465 13.8368 9.94439 13.8244 9.85793 13.776C9.77148 13.7276 9.70763 13.647 9.68025 13.5518C9.65287 13.4566 9.66417 13.3544 9.7117 13.2675L11.0055 10.9537C11.1096 10.7836 11.2556 10.6431 11.4296 10.5456C11.6036 10.4481 11.7997 10.3969 11.9992 10.3969C12.1987 10.3969 12.3948 10.4481 12.5688 10.5456C12.7428 10.6431 12.8888 10.7836 12.993 10.9537L14.2267 13.1625L14.3392 12.855C14.3738 12.7625 14.4435 12.6874 14.5331 12.646C14.6227 12.6046 14.725 12.6002 14.8178 12.6338C14.9106 12.6674 14.9865 12.7362 15.0289 12.8254C15.0712 12.9145 15.0768 13.0168 15.0442 13.11L14.6467 14.1937C14.6156 14.2803 14.5536 14.3524 14.4726 14.396C14.3916 14.4396 14.2973 14.4517 14.208 14.43L13.053 14.1525C12.9571 14.1281 12.8747 14.0669 12.8236 13.9822C12.7725 13.8974 12.7568 13.796 12.78 13.6998C12.8032 13.6036 12.8633 13.5205 12.9474 13.4683C13.0315 13.4162 13.1327 13.3992 13.2292 13.4212L13.5555 13.5L12.3367 11.3212C12.2985 11.2673 12.2479 11.2234 12.1891 11.193C12.1304 11.1627 12.0653 11.1469 11.9992 11.1469C11.9331 11.1469 11.868 11.1627 11.8093 11.193C11.7505 11.2234 11.6999 11.2673 11.6617 11.3212ZM15.258 18.3037H13.1842L13.3905 18.5212C13.4574 18.594 13.493 18.6903 13.4894 18.7892C13.4857 18.888 13.4432 18.9815 13.3711 19.0492C13.2989 19.1169 13.203 19.1534 13.1041 19.1507C13.0052 19.148 12.9114 19.1064 12.843 19.035L12.0442 18.1837C11.9791 18.114 11.943 18.0222 11.943 17.9268C11.943 17.8315 11.9791 17.7397 12.0442 17.67L12.843 16.8225C12.9107 16.7504 13.0043 16.7082 13.1031 16.7049C13.2019 16.7017 13.2981 16.7377 13.3704 16.8051C13.4428 16.8725 13.4855 16.9658 13.4893 17.0646C13.493 17.1635 13.4575 17.2598 13.3905 17.3325L13.1842 17.5537H15.258C15.498 17.5537 15.7117 17.3512 15.5955 17.1487L14.5942 15.36C14.5467 15.273 14.5354 15.1708 14.5627 15.0756C14.5901 14.9804 14.654 14.8998 14.7404 14.8514C14.8269 14.803 14.9289 14.7907 15.0244 14.8171C15.1199 14.8435 15.2012 14.9065 15.2505 14.9925L16.2517 16.7812C16.3359 16.9455 16.3752 17.1291 16.3656 17.3135C16.3559 17.4978 16.2977 17.6764 16.1968 17.831C16.0959 17.9855 15.9559 18.1107 15.791 18.1937C15.6261 18.2768 15.4422 18.3147 15.258 18.3037Z" fill="black"/>
<path d="M15.5767 3.6487C15.2542 4.2112 14.928 4.9837 14.3317 5.94745C12.6615 5.13157 11.1017 4.94497 9.41171 5.6587C9.01421 5.0887 8.61671 4.51495 8.21546 3.94495C7.42779 2.80924 8.99593 1.73962 10.053 1.6312C11.328 1.49995 11.7855 2.63245 13.638 2.55745C14.8971 2.50901 16.575 1.92268 15.5767 3.6487Z" fill="black"/>
`;

const arrow = `
<path d="M19.5 5L5.5 19" stroke="#FAFAFA" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M19.5 15.27V5H9.23" stroke="#FAFAFA" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
`;
</script>
<style>
.service-card-rich-text ul {
  margin-top: 5px;
  padding-left: 20px;
}

.service-card-rich-text ul li,
.service-card-rich-text ul p {
  margin: 0px;
  font-size: 12px;
  padding-left: 0px;
  @apply font-semibold;
}
</style>
